const asArray = value => (Array.isArray(value) ? value : []);

const compact = values => values.filter(value => value !== null && value !== undefined && value !== "");

const titleCase = value =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, letter => letter.toUpperCase());

const stripHtml = value =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();

const normalizeSchedule = availableDays =>
  Object.entries(availableDays || {}).flatMap(([day, value]) => {
    if (!value) return [];

    const hours = Array.isArray(value)
      ? value
          .map(interval => {
            if (typeof interval === "string") return interval;
            const start = interval?.start_time || interval?.startTime;
            const end = interval?.end_time || interval?.endTime;
            return compact([start, end]).join(" - ");
          })
          .filter(Boolean)
          .join(", ")
      : String(value);

    return hours ? [{ day: titleCase(day), hours }] : [];
  });

const consultationModeLabels = value => {
  const mode = Number(value);
  if (mode === 1) return ["Video consultation"];
  if (mode === 2) return ["Cabinet consultation"];
  return ["Video consultation", "Cabinet consultation"];
};

const paymentLabels = {
  0: "Cash and credit card",
  1: "Cash only",
  2: "Credit card only",
};

/** Converts the public API payload into the stable view model used by every profile section. */
export const normalizePublicDoctorProfile = payload => {
  const users = payload?.users || {};
  const firstName = users.first_name || "";
  const lastName = users.last_name || "";
  const fullName = compact([firstName, lastName]).join(" ");
  const lat = Number(users.lat);
  const lng = Number(users.lng);

  return {
    user: users,
    id: users.id,
    slug: users.slug,
    name: `Dr. ${fullName || "Doctor"}`,
    firstName,
    lastName,
    speciality: users.speciality || payload?.doctor_speciality || "Medical practitioner",
    profileImage: users.profile_pic_url,
    rating: Number(users.doctor_rating || payload?.averageRating || 0),
    reviewCount: asArray(users.reviewlist).length,
    isBookable: Number(users.status) === 1,
    serviceDescription: users.service_description || "",
    priceDescription: users.price_description || "",
    paymentLabel: paymentLabels[Number(users.payment_type)] || "Contact the practice",
    consultationModes: consultationModeLabels(users.videos),
    phoneNumbers: compact([users.cell_phone, users.cell_phone1, users.cell_phone2]),
    location: {
      address: compact([users.cabin_address, users.municiple, users.city]).join(", "),
      coordinates: Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null,
      transports: asArray(users.doctor_transports).map(item => item.transport).filter(Boolean),
      parkings: asArray(users.doctor_parkings).map(item => item.parking).filter(Boolean),
      usefulInformation: asArray(users.doctor_informations).map(item => item.information).filter(Boolean),
    },
    schedule: normalizeSchedule(users.availableDays),
    expertise: asArray(users.doctor_experts).map(item => item.expertise).filter(Boolean),
    rates: asArray(users.doctor_consultations).map(item => ({
      label: item.consultation,
      fees: item.fees,
    })),
    languages: asArray(payload?.doctor_languages)
      .map(item => item?.language?.name || item?.name)
      .filter(Boolean),
    gallery: asArray(users.doctor_documents)
      .map(item => item.documents_url)
      .filter(Boolean),
    degrees: asArray(users.doctor_degrees),
    trainings: asArray(users.doctor_trainings),
    experiences: asArray(users.doctor_experiences),
    publications: asArray(users.doctor_publications),
    faqs: asArray(payload?.doctor_faqs).map(item => ({
      question: item?.faq?.name || item?.question || "Frequently asked question",
      answer: stripHtml(item?.answer),
    })),
    reviews: asArray(users.reviewlist),
    notice: stripHtml(payload?.showmessage),
  };
};

/** Preserves the current search while making the viewed doctor explicit for the booking flow. */
export const buildDoctorBookingContext = (currentContext, doctor) => ({
  ...(currentContext || {}),
  type: "doctor",
  types: 0,
  slug: doctor?.slug,
  user_id: doctor?.id,
});

export const getMapsUrl = coordinates =>
  coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`
    : null;

export const getPublicDoctorProfilePath = slug => (slug ? `/doctors/${encodeURIComponent(slug)}` : null);
